import React from "react";
import {
    Paper, Toolbar, Typography, TextField, Table, TableHead, TableRow, TableCell,
    TableBody, TableContainer, TablePagination, IconButton, Tooltip, CircularProgress,
    Stack, Chip, Button
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}` });

/* ---- helpers totales ---- */
const readHeaderTotal = (res) => {
    const h = res?.headers || {};
    const keys = [
        "x-total-count", "x-total", "x-total-elements", "x-totalrecords", "x-total-pages-items",
        "x-totalitems", "x-total-items"
    ];
    for (const k of keys) {
        const v = h[k];
        if (v != null && !Number.isNaN(parseInt(v, 10))) return parseInt(v, 10);
    }
    return null;
};

const formatDDMMYYYY = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
};

export default function AntiguoHistoricoAdmin() {
    // filtros
    const [filtro, setFiltro] = React.useState({ codigo: "", nombre: "", apellido_1: "", apellido_2: "", nif: "" });

    // datos/paginación
    const [rows, setRows] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [size, setSize] = React.useState(25);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);

    // 👇 NUEVO: toggle para mostrar más/menos columnas
    const [showMore, setShowMore] = React.useState(false);

    const buildParams = React.useCallback((p, s) => {
        return {
            page: p, size: s, sort: "fecha,desc",
            ...(filtro.codigo.trim() ? { codigo: parseInt(filtro.codigo, 10) || undefined } : {}),
            ...(filtro.nombre.trim() ? { nombre: filtro.nombre.trim() } : {}),
            ...(filtro.apellido_1.trim() ? { apellido_1: filtro.apellido_1.trim() } : {}),
            ...(filtro.apellido_2.trim() ? { apellido_2: filtro.apellido_2.trim() } : {}),
            ...(filtro.nif.trim() ? { nif: filtro.nif.trim() } : {}),
        };
    }, [filtro]);

    const fetchPage = React.useCallback(async () => {
        setLoading(true);
        try {
            const params = buildParams(page, size);
            const res = await axios.get(`${API}/api/admin/historico-antiguo`, {
                headers: authHeaders(), params
            });
            const data = res?.data || {};
            const content = Array.isArray(data?.content) ? data.content : (Array.isArray(data) ? data : []);
            setRows(content);

            let t = Number.isFinite(Number(data?.totalElements)) ? Number(data.totalElements) : null;
            if (t == null) {
                const ht = readHeaderTotal(res);
                if (ht != null) t = ht;
            }
            if (t == null) {
                if (content.length < size) t = page * size + content.length;
                else {
                    const peek = await axios.get(`${API}/api/admin/historico-antiguo`, {
                        headers: authHeaders(), params: buildParams(page + 1, 1)
                    });
                    const hasNext = Array.isArray(peek?.data?.content)
                        ? peek.data.content.length > 0
                        : (Array.isArray(peek?.data) ? peek.data.length > 0 : false);
                    t = hasNext ? ((page + 2) * size) : ((page + 1) * size);
                }
            }
            const maxPage = Math.max(0, Math.ceil(t / size) - 1);
            if (page > maxPage) setPage(maxPage);
            setTotal(t);
        } catch {
            setTotal(page * size + (rows?.length || 0));
        } finally {
            setLoading(false);
        }
    }, [API, page, size, buildParams]);

    React.useEffect(() => { fetchPage(); }, [fetchPage]);

    const onChangeFiltro = (field) => (e) => {
        setPage(0);
        setFiltro((f) => ({ ...f, [field]: e.target.value }));
    };

    const exportToExcel = async () => {
        try {
            const pageSize = 1000;
            let p = 0, acc = [], totalPages = 1;
            do {
                const res = await axios.get(`${API}/api/admin/historico-antiguo`, {
                    headers: authHeaders(), params: buildParams(p, pageSize)
                });
                const content = Array.isArray(res?.data?.content) ? res.data.content
                    : (Array.isArray(res?.data) ? res.data : []);
                acc = acc.concat(content);

                let t = Number(res?.data?.totalElements);
                if (!Number.isFinite(t)) {
                    const ht = readHeaderTotal(res);
                    if (ht != null) t = ht;
                }
                totalPages = Number.isFinite(t) ? Math.max(1, Math.ceil(t / pageSize))
                    : (content.length < pageSize ? p + 1 : p + 2);
                p += 1;
            } while (p < totalPages);

            if (!acc.length) return;

            // Si showMore está activo, exportamos con todas las columnas; si no, solo compacto
            const full = showMore;
            const sheetRows = acc.map((r) => full ? ({
                Fecha: formatDDMMYYYY(r.fecha),
                Hora: r.hora || "",
                Fecha_est: formatDDMMYYYY(r.fecha_est),
                Tipo: r.tipo_cambio || "",
                Codigo: r.colegiado ?? "",
                Nombre: r.nombre || "", Apellido_1: r.apellido_1 || "", Apellido_2: r.apellido_2 || "",
                NIF: r.cif || "", Sexo: r.sexo || "", LOPD: r.lopd || "",
                Fecha_nac: formatDDMMYYYY(r.fecha_nac), Pais_nac: r.pais_nac || "", Region_nac: r.region_nac || "", Localidad_nac: r.localidad_nac || "",
                Dir: r.dir || "", Localidad: r.localidad || "", CP: r.cp || "",
                Tlfno_1: r.tlfno_1 || "", Tlfno_2: r.tlfno_2 || "", Tlfno_3: r.tlfno_3 || "",
                Fax: r.fax || "", Email_1: r.email_1 || "", Email_2: r.email_2 || "", Email_3: r.email_3 || "",
                Banco: r.banco || "", Sucursal: r.sucursal || "", DC: r.dc || "", CCC: r.ccc || "",
                Cuota: r.cuota ?? "", Estado: r.estado || "", Usuario: r.usuario || "",
            }) : ({
                Fecha: formatDDMMYYYY(r.fecha),
                Tipo: r.tipo_cambio || "",
                Codigo: r.colegiado ?? "",
                Nombre: [r.nombre, r.apellido_1, r.apellido_2].filter(Boolean).join(" "),
                NIF: r.cif || "",
                Localidad: r.localidad || "",
                Estado: r.estado || "",
                Usuario: r.usuario || "",
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(sheetRows);
            XLSX.utils.book_append_sheet(wb, ws, "AntiguoHistorico");
            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            saveAs(new Blob([wbout], { type: "application/octet-stream" }),
                `antiguo_historico_${showMore ? "completo_" : ""}${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch { }
    };

    const countForPagination = Number.isFinite(Number(total))
        ? Number(total)
        : (page * size + (rows?.length || 0));

    return (
        <Paper sx={{ p: 0, overflow: "hidden" }}>
            <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Antiguo Histórico (solo lectura)
                </Typography>

                {/* 👇 NUEVO: botón para alternar columnas */}
                <Button
                    variant={showMore ? "contained" : "outlined"}
                    onClick={() => setShowMore((v) => !v)}
                >
                    {showMore ? "Menos" : "Más información"}
                </Button>

                <TextField size="small" label="Código" value={filtro.codigo} onChange={onChangeFiltro("codigo")} sx={{ width: 130 }} />
                <TextField size="small" label="Nombre" value={filtro.nombre} onChange={onChangeFiltro("nombre")} sx={{ width: 180 }} />
                <TextField size="small" label="Apellido 1" value={filtro.apellido_1} onChange={onChangeFiltro("apellido_1")} sx={{ width: 180 }} />
                <TextField size="small" label="Apellido 2" value={filtro.apellido_2} onChange={onChangeFiltro("apellido_2")} sx={{ width: 180 }} />
                <TextField size="small" label="NIF" value={filtro.nif} onChange={onChangeFiltro("nif")} sx={{ width: 160 }} />

                <Tooltip title="Refrescar">
                    <IconButton onClick={fetchPage}><RefreshIcon /></IconButton>
                </Tooltip>
                <Button variant="outlined" onClick={exportToExcel}>Exportar Excel</Button>
                <Button
                    variant="text"
                    onClick={() => { setFiltro({ codigo: "", nombre: "", apellido_1: "", apellido_2: "", nif: "" }); setPage(0); }}
                >
                    Limpiar filtros
                </Button>
            </Toolbar>

            {/* para muchas columnas, dejamos scroll horizontal */}
            <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {/* columnas base */}
                            <TableCell>Fecha</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Código</TableCell>
                            <TableCell>Nombre completo</TableCell>
                            <TableCell>NIF</TableCell>
                            <TableCell>Localidad</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Usuario</TableCell>

                            {/* 👇 columnas extendidas */}
                            {showMore && (
                                <>
                                    <TableCell>Hora</TableCell>
                                    <TableCell>Fecha_est</TableCell>
                                    <TableCell>Fecha_nac</TableCell>
                                    <TableCell>Pais_nac</TableCell>
                                    <TableCell>Region_nac</TableCell>
                                    <TableCell>Localidad_nac</TableCell>
                                    <TableCell>Dir</TableCell>
                                    <TableCell>CP</TableCell>
                                    <TableCell>Tlfno_1</TableCell>
                                    <TableCell>Tlfno_2</TableCell>
                                    <TableCell>Tlfno_3</TableCell>
                                    <TableCell>Fax</TableCell>
                                    <TableCell>Email_1</TableCell>
                                    <TableCell>Email_2</TableCell>
                                    <TableCell>Email_3</TableCell>
                                    <TableCell>Banco</TableCell>
                                    <TableCell>Sucursal</TableCell>
                                    <TableCell>DC</TableCell>
                                    <TableCell>CCC</TableCell>
                                    <TableCell>Cuota</TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={showMore ? 33 : 8} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                        ) : (rows.length ? rows.map((r) => (
                            <TableRow key={r.id} hover>
                                {/* base */}
                                <TableCell>{formatDDMMYYYY(r.fecha)}</TableCell>
                                <TableCell>{r.tipo_cambio || ""}</TableCell>
                                <TableCell>{r.colegiado ?? ""}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography fontWeight={600} noWrap title={[r.nombre, r.apellido_1, r.apellido_2].filter(Boolean).join(" ")}>
                                            {[r.nombre, r.apellido_1, r.apellido_2].filter(Boolean).join(" ")}
                                        </Typography>
                                        {r.sexo ? <Chip size="small" label={r.sexo} variant="outlined" /> : null}
                                    </Stack>
                                </TableCell>
                                <TableCell>{r.cif || ""}</TableCell>
                                <TableCell title={r.localidad || ""}>{r.localidad || ""}</TableCell>
                                <TableCell>{r.estado || ""}</TableCell>
                                <TableCell>{r.usuario || ""}</TableCell>

                                {/* extendidas */}
                                {showMore && (
                                    <>
                                        <TableCell>{r.hora || ""}</TableCell>
                                        <TableCell>{formatDDMMYYYY(r.fecha_est)}</TableCell>
                                        <TableCell>{formatDDMMYYYY(r.fecha_nac)}</TableCell>
                                        <TableCell>{r.pais_nac || ""}</TableCell>
                                        <TableCell>{r.region_nac || ""}</TableCell>
                                        <TableCell>{r.localidad_nac || ""}</TableCell>
                                        <TableCell title={r.dir || ""}>{r.dir || ""}</TableCell>
                                        <TableCell>{r.cp || ""}</TableCell>
                                        <TableCell>{r.tlfno_1 || ""}</TableCell>
                                        <TableCell>{r.tlfno_2 || ""}</TableCell>
                                        <TableCell>{r.tlfno_3 || ""}</TableCell>
                                        <TableCell>{r.fax || ""}</TableCell>
                                        <TableCell title={r.email_1 || ""}>{r.email_1 || ""}</TableCell>
                                        <TableCell title={r.email_2 || ""}>{r.email_2 || ""}</TableCell>
                                        <TableCell title={r.email_3 || ""}>{r.email_3 || ""}</TableCell>
                                        <TableCell>{r.banco || ""}</TableCell>
                                        <TableCell>{r.sucursal || ""}</TableCell>
                                        <TableCell>{r.dc || ""}</TableCell>
                                        <TableCell title={r.ccc || ""}>{r.ccc || ""}</TableCell>
                                        <TableCell>{r.cuota ?? ""}</TableCell>
                                    </>
                                )}
                            </TableRow>
                        )) : (
                            <TableRow><TableCell colSpan={showMore ? 33 : 8} align="center">
                                <Typography color="text.secondary">Sin resultados.</Typography>
                            </TableCell></TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={Number.isFinite(Number(total)) ? Number(total) : (page * size + (rows?.length || 0))}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={size}
                onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Filas por página"
            />
        </Paper>
    );
}
